import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import { env } from "@/env";
import { Vector } from "@/models/vector";
import { Plane } from "@/models/plane";
import DreszcodeConfiguration from "@/assets/json/DreszcodeConfiguration.json";
import { BodyprintMode } from "@/enums";

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    api_url: env.VUE_APP_API_URL,
    auth: {
      loggedIn: false,
      userId: null,
      userEmail: null,
      noPermission: false,
      bodyprintQueue: false,
      bodyprintSearch: false,
      bodyprintList: false,
      authenticationError: false
    },
    loading: true,
    mode: BodyprintMode.NONE,
    zoom: {},
    queueItem: {},
    queueEmpty: false,
    magnifier: true,
    imageSide: null,
    imageBack: null,
    measureImage: {},
    measureStep: 0,
    growthFactor: "",
    modifiedPoints: [],
    activeCoordinates: {},
    activePoints: {},
    newCoordinates: {},
    windowHeight: window.innerHeight - 120,
    imageHeight: 1000,
    imageWidth: 750,
    queueSize: 0,
    isQueueItem: false,
    queue: {},
    defaultPoints: DreszcodeConfiguration["initialPoints"],
    lines: DreszcodeConfiguration["lines"],
    intersections: DreszcodeConfiguration["intersections"],
    areas: DreszcodeConfiguration["areas"],
    constraints: DreszcodeConfiguration["constraints"],
    currentStep: "review",
    bodyprintSizes: [],
    bodyprints: [],
    bodyprint: null
    // {
    // bodylength: 180,
    // gender: "m",
    // guides: [
    //   {
    //     crop: {
    //       height: 0.6025641,
    //       width: 0.28055555,
    //       x: 0.35925925,
    //       y: 0.2006689,
    //     },
    //     image_id: "5dc148b8ae4100002a0e67f0",
    //     image_source: "lady_orange_back.png",
    //     projection: "back",
    //     rotation: {
    //       pitch: 0.0,
    //       quaternion: {
    //         w: 0.0,
    //         x: 0.0,
    //         y: 0.0,
    //         z: 0.0,
    //       },
    //       roll: 0.0,
    //       yaw: 0.0,
    //     },
    //   },
    //   {
    //     crop: {
    //       height: 0.622631,
    //       width: 0.12037037,
    //       x: 0.42962962,
    //       y: 0.18673356,
    //     },
    //     image_id: "5dc148b9ae4100006a479cfb",
    //     image_source: "lady_orange_side.png",
    //     projection: "left",
    //     rotation: {
    //       pitch: 0.0,
    //       quaternion: {
    //         w: 0.0,
    //         x: 0.0,
    //         y: 0.0,
    //         z: 0.0,
    //       },
    //       roll: 0.0,
    //       yaw: 0.0,
    //     },
    //   },
    // ],
    // points: [],
    // }
  },
  getters: {
    getAuthLoggedIn: state => {
      return state.auth.loggedIn;
    },
    getAuth: state => {
      return state.auth;
    },
    isQueueItem: state => {
      return state.isQueueItem;
    },
    getMagnifier: state => {
      return state.magnifier;
    },
    getImage: state => projection => {
      if (projection === "left") {
        return state.imageSide;
      } else {
        return state.imageBack;
      }
    },
    getMeasureImage: state => {
      return state.measureImage;
    },
    // getActivePoints: (state, getters) => {
    //   this.getArea.handles[i]
    //   return state.activePoints;
    // },
    getBodyprintSizes: state => {
      return state.bodyprintSizes;
    },
    getBodyPrint: state => {
      return state.bodyprint;
    },
    getBodyPrints: state => {
      return state.bodyprints;
    },
    getGrowthFactor: state => {
      return state.windowHeight / state.imageHeight;
    },
    // getSideGuides: state => {
    //   return state.bodyprint.guides.find(guide => guide.projection === "left");
    // },
    getSideWidth: (state, getters) => {
      return (
        getters.getSideGuides.crop.width *
        state.imageWidth *
        state.getGrowthFactor
      );
    },
    getBackWidth: (state, getters) => {
      return (
        getters.getBackGuides.crop.width *
        state.imageWidth *
        state.getGrowthFactor
      );
    },
    getPictureScale: state => {
      return state.windowHeight / state.imageHeight;
    },
    getPictureWidth: (state, getters) => {
      return state.imageWidth * getters.getPictureScale;
    },
    getPictureHeight: state => {
      return state.windowHeight;
    },
    getBackGuides: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "back");
    },
    // getPoints: (state) => {
    //   return state.bodyprint.points; // currently not used.
    // },
    getPoint: state => name => {
      let point = state.bodyprint.points.find(point => point.name === name);
      if (point) {
        return {
          name: point.name,
          y: point.y,
          x: point.x,
          z: point.z
        };
      }
      return state.defaultPoints.find(point => point.name === name);
    },
    getPointCoordinates: (state, getters) => (name, projection) => {
      const point = getters.getPoint(name);
      // normalize here...
      return {
        y: point.y,
        x: projection === "back" ? point.x : point.z
      };
    },
    getCurrentStep: state => {
      return state.currentStep;
    },
    getSelectedMeasurePoint: state => {
      return state.selectedMeasurePoint;
    },
    getNewCoordinates: state => {
      return state.activeCoordinates;
    },
    getLines: state => {
      return state.lines;
    },
    getCropBack: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "back");
    },
    getCropSide: state => {
      return state.bodyprint.guides.find(guide => guide.projection === "left");
    },
    getGuide: state => projection => {
      return state.bodyprint.guides.find(
        guide => guide.projection === projection
      );
    },
    getGrowthFactorCrop: state => {
      return state.windowHeight / (state.height * state.windowHeight);
    },
    getMeasureStep: state => {
      return state.measureStep;
    },
    getPointsByArea: (state, getters) => {
      const area = getters.getArea;
      var pointCoordinates = [];
      for (var i = 0; i < area.handles.length; i++) {
        pointCoordinates.push({
          side: area.projection,
          name: area.handles[i].name,
          puppetPointName: area.handles[i].puppetPointName,
          coordinates: getters.getPointCoordinates(
            area.handles[i].puppetPointName,
            area.projection
          )
        });
      }
      return pointCoordinates;
    },
    getArea: state => {
      const step = state.measureStep;
      return state.areas[step];
    },
    getAreaLength: state => {
      return state.areas.length;
    },
    getLoading: state => {
      return state.loading;
    },
    getQueueItem: state => {
      return state.queueItem;
    },
    getQueueEmpty: state => {
      return state.queueEmpty;
    },
    getBodyprintMode: state => {
      return state.mode;
    }
  },
  mutations: {
    CHECK(state) {
      state.authenticated = !!localStorage.getItem("auth_token");
      if (state.authenticated) {
        state.auth.loggedIn = true;
        state.auth.userId = localStorage.getItem("auth_userid");
        state.auth.userEmail = localStorage.getItem("auth_useremail");
        state.auth.bodyprintQueue =
          localStorage.getItem("auth_bodyprintQueue") === "true";
        state.auth.bodyprintSearch =
          localStorage.getItem("auth_bodyprintSearch") === "true";
        state.auth.bodyprintList =
          localStorage.getItem("auth_bodyprintList") === "true";
        axios.defaults.headers.common.Authorization = `Basic ${localStorage.getItem(
          "auth_token"
        )}`;
        axios.defaults.baseURL = `${state.api_url}/api/v2/`;
        axios.defaults.headers.common["X-DOMAIN"] = window.location.host;
        Vue.prototype.$http = axios;
      }
    },
    LOGIN(state, payload) {
      state.auth.loggedIn = true;
      state.auth.userId = payload.result.data._id;
      state.auth.userEmail = payload.user.data.email;
      state.auth.bodyprintQueue = !!payload.bodyprintQueue;
      state.auth.bodyprintSearch = !!payload.bodyprintSearch;
      state.auth.bodyprintList = !!payload.bodyprintList;
      state.auth.authenticationError = false;
      localStorage.setItem("auth_token", payload.token);
      localStorage.setItem("auth_bodyprintQueue", state.auth.bodyprintQueue);
      localStorage.setItem("auth_bodyprintSearch", state.auth.bodyprintSearch);
      localStorage.setItem("auth_bodyprintList", state.auth.bodyprintList);
      localStorage.setItem("auth_userid", state.auth.userId);
      localStorage.setItem("auth_useremail", state.auth.userEmail);
      axios.defaults.headers.common.Authorization = `Basic ${payload.token}`;
      axios.defaults.baseURL = `${state.api_url}/api/v2/`;
      axios.defaults.headers.common["X-DOMAIN"] = window.location.host;
      Vue.prototype.$http = axios;
    },
    LOGOUT(state) {
      (state.bodyprintSizes = []), (state.bodyprints = []);
      state.bodyprint = {};
      state.auth.loggedIn = false;
      state.auth.userId = false;
      state.auth.userEmail = false;
      state.auth.bodyprintQueue = false;
      state.auth.bodyprintSearch = false;
      state.auth.bodyprintList = false;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_bodyprintQueue");
      localStorage.removeItem("auth_bodyprintSearch");
      localStorage.removeItem("auth_bodyprintList");
      localStorage.removeItem("auth_userid");
      localStorage.removeItem("auth_useremail");
      axios.defaults.headers.common.Authorization = "";
      Vue.prototype.$http = axios;
    },
    NOPERMISSION(state) {
      state.auth.noPermission = true;
    },
    AUTHENTICATIONERROR(state, payload) {
      state.auth.authenticationError = payload;
    },
    setQueueEmpty(state, payload) {
      state.queueEmpty = payload;
    },
    setQueueItem(state, payload) {
      state.isQueueItem = true;
      state.queueItem = payload;
    },
    setWindowHeight(state, payload) {
      // change 84 to desired
      let newHeight = payload.height - 84;
      let newScale = newHeight / state.imageHeight;
      let newWidth = state.imageWidth * newScale;
      if (newWidth < payload.width / 3) {
        state.windowHeight = newHeight;
      }
    },
    setZoom(state, payload) {
      state.zoom = payload;
    },
    // setModifiedPoint(state, payload) {
    //   console.log('modified point');
    //   console.log(state);
    //   console.log(payload);
    // },
    setMeasureImage(state, payload) {
      state.measureImage = payload;
    },
    SET_IMAGE_WIDTH(state, width) {
      state.imageWidth = width;
    },
    SET_IMAGE_HEIGHT(state, height) {
      state.imageHeight = height;
    },
    setCrop(state, payload) {
      payload[0].width = payload[0].x2 - payload[0].x;
      payload[0].height = payload[0].y2 - payload[0].y;
      payload[0].growthFactor =
        state.windowHeight / (payload[0].height * state.windowHeight);
      let guide = state.bodyprint.guides.find(
        guide => guide.projection === payload[1]
      );
      guide.crop = payload[0];
    },
    setCurrentStep(state, payload) {
      state.currentStep = payload;
    },
    updateConstraints(state, payload) {
      const point_0_index = state.bodyprint.points.findIndex(
        point => point.name === "point_0"
      );

      const point_38_index = state.bodyprint.points.findIndex(
        point => point.name === "point_38"
      );
      if (point_0_index !== -1) {
        const point_0 = state.bodyprint.points[point_0_index];

        if (point_0.y !== 0.0) {
          const guideIndex = state.bodyprint.guides.findIndex(
            guide => guide.projection === payload.projection
          );
          const guide = state.bodyprint.guides[guideIndex];
          const mutation = point_0.y * guide.crop.height;

          guide.crop.y = guide.crop.y + mutation;
          guide.crop.height = guide.crop.height - mutation;

          guide.crop.growthFactor =
            state.windowHeight / (guide.crop.height * state.windowHeight);
          Vue.set(state.bodyprint.guides, guideIndex, guide);

          point_0.y = 0.0;
          Vue.set(state.bodyprint.points, point_0_index, point_0);
        }
      }

      if (point_38_index !== -1) {
        const point_38 = state.bodyprint.points[point_38_index];
        if (point_38.y !== 1.0) {
          const guideIndex = state.bodyprint.guides.findIndex(
            guide => guide.projection === payload.projection
          );
          const guide = state.bodyprint.guides[guideIndex];
          const mutation = (1 - point_38.y) * guide.crop.height;

          guide.crop.height = guide.crop.height - mutation;
          guide.crop.growthFactor =
            state.windowHeight / (guide.crop.height * state.windowHeight);

          Vue.set(state.bodyprint.guides, guideIndex, guide);

          point_38.y = 1.0;
          Vue.set(state.bodyprint.points, point_38_index, point_38);
        }
      }
    },
    setPoint(state, payload) {
      const newPoint = {
        name: payload.name,
        x: payload.x,
        y: payload.y,
        z: payload.z
      };
      const index = state.bodyprint.points.findIndex(
        point => point.name === payload.name
      );
      if (index !== -1) {
        Vue.set(state.bodyprint.points, index, newPoint);
        return;
      }
      return state.bodyprint.points.push(newPoint);
    },
    setActivePoints(state, payload) {
      state.activePoints = payload;
    },
    deactivatePoints(state) {
      state.activeCoordinates = {};
    },
    setActiveCoordinates(state, payload) {
      state.activeCoordinates = payload;
    },
    setNewMeasureCoordinates(state, payload) {
      var point = state.activeCoordinates;
      if (payload === "top" || payload === "bottom") {
        if (point.canMoveY) {
          point.y = payload === "top" ? point.y - 1 : point.y + 1;
        }
      }
      if (payload === "left" || payload === "right") {
        if (point.canMoveX) {
          point.x = payload === "left" ? point.x - 1 : point.x + 1;
        }
      }
      state.activeCoordinates = point;
      //watcher..
      state.newCoordinates = point;

      return point;
    },
    setNewCropCoordinates(state, payload) {
      var position = state.activeCoordinates;
      if (payload === "top" || payload === "bottom") {
        position.y = payload === "top" ? position.y - 1 : position.y + 1;
      }
      if (payload === "left" || payload === "right") {
        position.x = payload === "left" ? position.x - 1 : position.x + 1;
      }
      state.activeCoordinates = position; //update new coordinates to selected item.
      state.newCoordinates = position; //update new coordinates for triggering watcher.
    },
    setMeasureStep(state, payload) {
      payload.step >= state.areas.length ? (payload.step = 0) : false;
      payload.step < 0 ? (payload.step = 20) : false;
      state.measureStep = payload.step;
    },
    setImages(state, images) {
      var backguid = state.bodyprint.guides.find(x => x.projection === "back");
      var leftguid = state.bodyprint.guides.find(x => x.projection === "left");
      state.imageBack = images.find(x => x._id === backguid.image_id).data;
      state.imageSide = images.find(x => x._id === leftguid.image_id).data;
    },
    setBodyprintSizes(state, sizes) {
      state.bodyprintSizes = sizes;
    },
    setBodyprint(state, bodyprint) {
      state.bodyprint = bodyprint;
    },
    setBodyprints(state, bodyprints) {
      state.bodyprints = bodyprints;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setMode(state, mode) {
      state.mode = mode;
    }
  },
  actions: {
    authenticate({ commit, state }, payload) {
      axios
        .get(`${state.api_url}/api/v2/authenticate`, {
          headers: { Authorization: `Basic ${payload}` }
        })
        .then(call => {
          if (call.data.status === "ACTIVE") {
            axios
              .get(`${state.api_url}/api/v2/permission/me`, {
                headers: { Authorization: `Basic ${payload}` }
              })
              .then(resp => {
                if (resp.data.find(x => x.permission === "measurePortal")) {
                  var bodyprintQueue = resp.data.find(
                    x => x.permission === "measurePortalBodyprintQueue"
                  );
                  var bodyprintSearch = resp.data.find(
                    x => x.permission === "measurePortalBodyprintSearch"
                  );
                  var bodyprintList = resp.data.find(
                    x => x.permission === "measurePortalBodyprintList"
                  );
                  axios
                    .get(`${state.api_url}/api/v2/user/${call.data._id}`, {
                      headers: { Authorization: `Basic ${payload}` }
                    })
                    .then(user => {
                      commit("LOGIN", {
                        token: payload,
                        user: user,
                        result: call,
                        bodyprintQueue: bodyprintQueue,
                        bodyprintSearch: bodyprintSearch,
                        bodyprintList: bodyprintList
                      });
                    });
                } else {
                  commit("NOPERMISSION");
                }
              });
          }
        })
        .catch(() => {
          commit("AUTHENTICATIONERROR", true);
        });
    },
    authcheck({ commit }) {
      commit("CHECK");
    },
    authlogout({ commit }) {
      commit("LOGOUT");
    },
    requestpasswordreset(_, username) {
      axios.get(`user/${username}/request_password_reset_v2`);
    },
    resetpassword(_, payload) {
      axios.post(`user/${payload.email}/reset_password`, {
        password: payload.password,
        token: payload.code
      });
    },
    async setBodyprint({ commit }, bodyprint) {
      //logic for set to normal
      commit("setBodyprint", bodyprint);
    },
    setQueueItemStatus({ getters }, payload) {
      var id = getters.getQueueItem._id;
      return axios
        .put(`bodyprint_queue/${id}`, payload, {
          headers: {
            // Overwrite Axios's automatically set Content-Type
            "Content-Type": "application/json"
          }
        })
        .then(() => {
          return true;
        });
    },
    cancelQueueItem({ getters }) {
      var id = getters.getQueueItem._id;
      return axios.get(`bodyprint_queue/${id}/cancel`).then(() => {
        return true;
      });
    },
    checkBodyprint({ dispatch, commit }) {
      commit("setMode", BodyprintMode.VIEW);
      dispatch("calculateBodyprint");
    },
    editBodyprint({ dispatch, commit }) {
      commit("setMode", BodyprintMode.EDIT);
      dispatch("calculateBodyprint");
    },
    calculateBodyprint({ commit, state, getters }) {
      const bodyprint = JSON.parse(JSON.stringify(state.bodyprint));
      var img = new Image();
      img.src = state.imageSide;

      var imgBack = new Image();
      var imgSide = new Image();

      imgBack.src = state.imageBack;
      imgSide.src = state.imageSide;

      setTimeout(() => {
        commit("SET_IMAGE_WIDTH", img.width);
        commit("SET_IMAGE_HEIGHT", img.height);

        let growthFactorSide =
          imgSide.height /
          (state.bodyprint.guides.find(x => x.projection === "left").crop
            .height *
            imgSide.height);

        let growthFactorBack =
          imgBack.height /
          (state.bodyprint.guides.find(x => x.projection === "back").crop
            .height *
            imgBack.height);

        let cropWidthSide = bodyprint.guides[0].crop.width;
        let cropWidthBack = bodyprint.guides[1].crop.width;
        var pointsProcessed = 0;
        if (bodyprint.points.length == 0) {
          commit("setLoading", false);
        }
        bodyprint.points.forEach(function(part) {
          pointsProcessed++;
          part.x =
            (part.x /
              (getters.getPictureWidth * growthFactorBack * cropWidthSide)) *
            (state.windowHeight / 1000);
          part.z =
            (part.z /
              (getters.getPictureWidth * growthFactorSide * cropWidthBack)) *
            (state.windowHeight / 1000);
          part.y = part.y / 1000;

          if (bodyprint.points.length === pointsProcessed) {
            commit("setBodyprint", bodyprint);
            setTimeout(() => {
              commit("setLoading", false);
            }, 1000);
          }
          if (cropWidthSide) {
            bodyprint.guides[0].crop.x2 =
              bodyprint.guides[0].crop.x + cropWidthSide;
            bodyprint.guides[0].crop.y2 =
              bodyprint.guides[0].crop.y + cropWidthSide;
            bodyprint.guides[0].crop.growthFactor =
              state.windowHeight /
              (bodyprint.guides[0].crop.height * state.windowHeight);
            bodyprint.guides[1].crop.x2 =
              bodyprint.guides[1].crop.x + cropWidthBack;
            bodyprint.guides[1].crop.y2 =
              bodyprint.guides[1].crop.y + cropWidthBack;
            bodyprint.guides[1].crop.growthFactor =
              state.windowHeight /
              (bodyprint.guides[1].crop.height * state.windowHeight);
          }
        });
      }, 1000);
    },
    postBodyprint({ state, getters }) {
      var imgBack = new Image();
      var imgSide = new Image();

      imgBack.src = state.imageBack;
      imgSide.src = state.imageSide;

      let growthFactorSide =
        imgSide.height /
        (state.bodyprint.guides.find(x => x.projection === "left").crop.height *
          imgSide.height);

      let growthFactorBack =
        imgBack.height /
        (state.bodyprint.guides.find(x => x.projection === "back").crop.height *
          imgBack.height);

      let cropWidthSide = state.bodyprint.guides.find(
        x => x.projection === "left"
      ).crop.width;
      let cropWidthBack = state.bodyprint.guides.find(
        x => x.projection === "back"
      ).crop.width;

      let bodyprint = JSON.parse(JSON.stringify(state.bodyprint));
      let bodyprintCopy = { ...bodyprint };

      var pointsProcessed = 0;
      bodyprintCopy.points.forEach(function(part) {
        pointsProcessed++;
        part.x =
          (part.x *
            (getters.getPictureWidth * growthFactorBack * cropWidthBack)) /
          (state.windowHeight / 1000);
        part.z =
          (part.z *
            (getters.getPictureWidth * growthFactorSide * cropWidthSide)) /
          (state.windowHeight / 1000);
        part.y = part.y * 1000;

        if (bodyprintCopy.points.length === pointsProcessed) {
          let id = state.bodyprint.id;
          if (!id) {
            id = state.bodyprint._id;
          }
          let user = state.bodyprint.user;
          let payload = bodyprintCopy;

          delete payload.status;
          delete payload.user;
          delete payload.id;
          delete payload._id;
          delete payload.last_updated_at;
          delete payload.created_at;
          delete payload.status_retailer;
          delete payload.completed_at;
          // payload.points = state.defaultPoints;

          axios.patch(`user/${user}/bodyprint/${id}`, payload);
        }
      });
    },
    checkQueue({ commit }, id) {
      return axios.get(`bodyprint_queue/check/${id}`).then(response => {
        if (response.data.inQueue) {
          var queueItem = response.data.item;
          commit("setQueueItem", queueItem);
        }
      });
    },
    getFirstInQueue({ commit }) {
      return axios
        .get(`bodyprint_queue/next`)
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data["MSG"]) {
              if (error.response.data["MSG"] === "QUEUE EMPTY") {
                commit("setQueueEmpty", true);
              }
            }
          }
        });
    },
    getBodyprintByUser({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/search-user`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    getBodyprintsByUser({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/queue-search-user`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          var bodyprints = response.data;
          commit("setBodyprints", bodyprints);
        });
    },
    getBodyprintsForRetailer({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/queue-search-for-retailer`, payload)
        .then(response => {
          var bodyprints = response.data;
          commit("setBodyprints", bodyprints);
        });
    },
    requestBodyprint({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/request-bodyprint`, {
          queue_id: payload.id
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    requestBodyprintToView({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/request-bodyprint-to-view`, {
          queue_id: payload.id
        })
        .then(response => {
          var bodyprint = response.data.body_print;
          commit("setBodyprint", bodyprint);
          commit("setQueueItem", response.data);
        });
    },
    requestBodyprintSizes({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/request-bodyprint-sizes`, {
          queue_id: payload.id
        })
        .then(response => {
          commit("setBodyprintSizes", response.data);
        });
    },
    getBodyprintByUserRaw({ commit }, payload) {
      return axios
        .post(`bodyprint_queue/search-user-sizes`, {
          email: payload.email,
          code: payload.code
        })
        .then(response => {
          commit("setBodyprintSizes", response.data);
        });
    },
    getBodyprint({ dispatch }, params) {
      return axios.get(`user/${params.id}/bodyprint`).then(response => {
        var bodyprint;
        if (params.bodyprint_id) {
          bodyprint = response.data.find(x => x.id === params.bodyprint_id);
        } else {
          bodyprint = response.data[response.data.length - 1];
        }
        return dispatch("setBodyprint", bodyprint);
      });
    },
    getImages({ commit }, id) {
      return axios
        .get(`admin-user/${id}/images`)
        .then(response => commit("setImages", response.data));
    },
    getBodyprintSizes({ commit }, params) {
      return axios
        .get(`user/${params.user}/measurement/${params.bodyprint}`)
        .then(response => {
          commit("setBodyprintSizes", response.data);
        })
        .catch(error => commit("setBodyprintSizes", error.response.data));
    },
    solveConstraint({ commit, getters }, constraint) {
      const payload = this.getters.getPoint(constraint.puppetPointName);
      let referenceValues = {
        x: 0,
        y: 0,
        z: 0
      };

      if (constraint.referencePointName) {
        const referencePoint = getters.getPoint(constraint.referencePointName);
        referenceValues = { ...referencePoint };
      }
      const axises = [];
      for (const axis in constraint.axis) {
        if (constraint.axis[axis]) {
          axises.push(axis);
        }
      }

      for (const index in axises) {
        const currentAxis = axises[index];
        if (constraint.relation === "<=") {
          if (
            payload[currentAxis] >
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === ">=") {
          if (
            payload[currentAxis] <
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === "=") {
          if (
            payload[currentAxis] !==
            referenceValues[currentAxis] + constraint.distance
          ) {
            payload[currentAxis] =
              referenceValues[currentAxis] + constraint.distance;
          }
        }
        if (constraint.relation === "avg") {
          const avgPoint = getters.getPoint(
            constraint.averageReferencePointName
          );
          payload[currentAxis] =
            (avgPoint[currentAxis] + referenceValues[currentAxis]) / 2;
        }
      }
      commit("setPoint", payload);
    },
    setPoint({ dispatch, commit, state }, point) {
      commit("setPoint", point);
      const constraints = state.constraints.filter(
        constraint => constraint.puppetPointName === point.name
      );

      for (let constraintIndex in constraints) {
        const constraint = constraints[constraintIndex];
        dispatch("solveConstraint", constraint);
      }

      for (let constraintIndex in state.constraints) {
        const constraint = state.constraints[constraintIndex];
        if (constraint.puppetPointName !== point.name) {
          dispatch("solveConstraint", constraint);
        }
      }

      // Update intersections
      state.intersections.forEach(intersection => {
        var line = intersection.line;
        var planarLineA = intersection.planarLineA;
        var planarLineB = intersection.planarLineB;

        if (
          point.name === line.startPointName ||
          point.name === line.endPointName ||
          point.name === planarLineA.startPointName ||
          point.name === planarLineA.endPointName ||
          point.name === planarLineB.startPointName ||
          point.name === planarLineB.endPointName
        ) {
          dispatch("solveIntersections", intersection);
        }
      });
      state.intersections.forEach(intersection => {
        var line = intersection.line;
        var planarLineA = intersection.planarLineA;
        var planarLineB = intersection.planarLineB;

        if (
          point.name !== line.startPointName &&
          point.name !== line.endPointName &&
          point.name !== planarLineA.startPointName &&
          point.name !== planarLineA.endPointName &&
          point.name !== planarLineB.startPointName &&
          point.name !== planarLineB.endPointName
        ) {
          dispatch("solveIntersections", intersection);
        }
      });
    },
    solveIntersections({ commit, getters }, intersection) {
      var puppetPoint = getters.getPoint(intersection.puppetPointName);
      var newPoint;

      var lineStartPoint = getters.getPoint(intersection.line.startPointName);
      var lineEndPoint = getters.getPoint(intersection.line.endPointName);

      var planarLineAStartPoint = getters.getPoint(
        intersection.planarLineA.startPointName
      );
      var planarLineAEndPoint = getters.getPoint(
        intersection.planarLineA.endPointName
      );
      var planarLineBStartPoint = getters.getPoint(
        intersection.planarLineB.startPointName
      );
      var planarLineBEndPoint = getters.getPoint(
        intersection.planarLineB.endPointName
      );

      var planarVectorA = new Vector.VectorToFrom(
        planarLineAStartPoint,
        planarLineAEndPoint
      );
      var planarVectorB = new Vector.VectorToFrom(
        planarLineBStartPoint,
        planarLineBEndPoint
      );

      var plane = new Plane(
        planarLineAStartPoint,
        planarVectorA,
        planarVectorB
      );

      var intersectionPoint = plane.planeIntersectPoints(
        lineStartPoint,
        lineEndPoint
      );
      intersectionPoint.name = puppetPoint.name;
      newPoint = intersectionPoint;

      commit("setPoint", newPoint);
    }
  }
});

export default store;
